import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useService } from '@/contexts/ServiceContext';
import { useAuth } from '@/hooks/useAuth';
import { AccountingService } from '@flowstate/core';
import type { SubscriptionTransaction } from '@flowstate/types';
import { toast } from '@/components/ui/sonner';
// Query keys for React Query
export const accountingSubscriptionKeys = {
  all: ['accounting', 'subscriptions'] as const,
  lists: () => [...accountingSubscriptionKeys.all, 'list'] as const,
  list: (userId?: string, status?: string) => [...accountingSubscriptionKeys.lists(), { userId, status }] as const,
  renewals: () => [...accountingSubscriptionKeys.all, 'renewals'] as const,
  renewal: (userId?: string, days?: number) => [...accountingSubscriptionKeys.renewals(), { userId, days }] as const,
  summary: () => [...accountingSubscriptionKeys.all, 'summary'] as const,
  userSummary: (userId?: string) => [...accountingSubscriptionKeys.summary(), userId] as const,
};

/**
 * Hook for fetching subscriptions from accounting module
 */
export const useAccountingSubscriptions = (params: {
  userId?: string;
  status?: string;
  includeInactive?: boolean;
} = {}) => {
  const accountingService = useService<AccountingService>('accounting');

  return useQuery({
    queryKey: accountingSubscriptionKeys.list(params.userId, params.status),
    queryFn: async () => {
      if (!accountingService) {throw new Error('Accounting service not ready');}
      const response = await accountingService.listSubscriptions(params);
      if (!response.success) {throw new Error(response.error);}
      return response.data || [];
    },
    enabled: !!accountingService,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for fetching user's own subscriptions
 */
export const useMyAccountingSubscriptions = () => {
  const { user } = useAuth();
  const accountingService = useService<AccountingService>('accounting');
  
  return useQuery({
    queryKey: accountingSubscriptionKeys.list(user?.id),
    queryFn: async () => {
      if (!accountingService) {throw new Error('Accounting service not ready');}
      const response = await accountingService.listSubscriptions({ userId: user?.id, includeInactive: false });
      if (!response.success) {throw new Error(response.error);}
      return response.data || [];
    },
    enabled: !!user?.id && !!accountingService,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook for fetching upcoming subscription renewals
 */
export const useUpcomingRenewals = (params: { userId?: string; days?: number } = {}) => {
  const { days = 30 } = params;
  const accountingService = useService<AccountingService>('accounting');
  
  return useQuery({
    queryKey: accountingSubscriptionKeys.renewal(params.userId, days),
    queryFn: async () => {
      if (!accountingService) {throw new Error('Accounting service not ready');}
      const response = await accountingService.getUpcomingRenewals(params);
      if (!response.success) {throw new Error(response.error);}
      return response.data || [];
    },
    enabled: !!accountingService,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook for fetching subscription cost summary
 */
export const useSubscriptionCostSummary = (userId?: string) => {
  const accountingService = useService<AccountingService>('accounting');

  return useQuery({
    queryKey: accountingSubscriptionKeys.userSummary(userId),
    queryFn: async () => {
      if (!accountingService) {throw new Error('Accounting service not ready');}
      const response = await accountingService.getSubscriptionCostSummary(userId);
      if (!response.success) {throw new Error(response.error);}
      return response.data;
    },
    enabled: !!accountingService,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook for creating subscriptions
 */
export const useCreateAccountingSubscription = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const accountingService = useService<AccountingService>('accounting');

  return useMutation({
    mutationFn: async (data: Omit<SubscriptionTransaction, 'id' | 'created_at' | 'type'>) => {
      if (!user?.id) {throw new Error('User not authenticated');}
      if (!accountingService) {throw new Error('Accounting service not ready');}
      const response = await accountingService.createSubscription({ ...data, user_id: user.id });
      if (!response.success) {throw new Error(response.error);}
      return response.data;
    },
    onSuccess: () => {
      // Invalidate all subscription queries
      queryClient.invalidateQueries({ queryKey: accountingSubscriptionKeys.all });
      toast.success('Success', { description: 'Subscription created successfully' });
    },
    onError: (error: Error) => {
      toast.error('Error', { description: error.message });
    },
  });
};

/**
 * Hook for updating subscriptions
 */
export const useUpdateAccountingSubscription = () => {
  const queryClient = useQueryClient();
  const accountingService = useService<AccountingService>('accounting');

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<SubscriptionTransaction> }) => {
      if (!accountingService) {throw new Error('Accounting service not ready');}
      const response = await accountingService.updateSubscription(id, updates);
      if (!response.success) {throw new Error(response.error);}
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountingSubscriptionKeys.all });
      toast.success('Success', { description: 'Subscription updated successfully' });
    },
    onError: (error: Error) => {
      toast.error('Error', { description: error.message });
    },
  });
};

/**
 * Hook for cancelling subscriptions
 */
export const useCancelAccountingSubscription = () => {
  const queryClient = useQueryClient();
  const accountingService = useService<AccountingService>('accounting');

  return useMutation({
    mutationFn: async (id: string) => {
      if (!accountingService) {throw new Error('Accounting service not ready');}
      const response = await accountingService.cancelSubscription(id);
      if (!response.success) {throw new Error(response.error);}
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountingSubscriptionKeys.all });
      toast.success('Success', { description: 'Subscription cancelled successfully' });
    },
    onError: (error: Error) => {
      toast.error('Error', { description: error.message });
    },
  });
};

/**
 * Utility hook for subscription cost calculations
 * Uses useMemo to optimize expensive calculations
 */
export const useSubscriptionCostCalculations = (subscriptions: SubscriptionTransaction[] = []) => {
  // Memoize the conversion function to avoid recreating it
  const convertToMonthly = useCallback((amount: number, cycle: string): number => {
    switch (cycle) {
      case 'monthly': return amount;
      case 'quarterly': return amount / 3;
      case 'yearly': return amount / 12;
      case 'weekly': return amount * 4.33;
      case 'daily': return amount * 30;
      default: return amount;
    }
  }, []);

  // Memoize active subscriptions filter (expensive operation)
  const activeSubscriptions = useMemo(() => {
    return subscriptions.filter(s => s.subscription_status === 'active' || !s.subscription_status);
  }, [subscriptions]);

  // Memoize total monthly calculation
  const totalMonthly = useMemo(() => {
    return activeSubscriptions.reduce((sum, sub) => 
      sum + convertToMonthly(sub.amount, sub.billing_cycle || 'monthly'), 0
    );
  }, [activeSubscriptions, convertToMonthly]);

  // Memoize total yearly (simple calculation, but memoized for consistency)
  const totalYearly = useMemo(() => totalMonthly * 12, [totalMonthly]);

  // Memoize cost by category (expensive reduce operation)
  const costByCategory = useMemo(() => {
    return activeSubscriptions.reduce((acc, sub) => {
      const categoryName = sub.categories_accounting?.[0]?.name || 'Uncategorized';
      const monthly = convertToMonthly(sub.amount, sub.billing_cycle || 'monthly');
      acc[categoryName] = (acc[categoryName] || 0) + monthly;
      return acc;
    }, {} as Record<string, number>);
  }, [activeSubscriptions, convertToMonthly]);

  // Memoize upcoming renewals (expensive filter + sort operation)
  const upcomingRenewals = useMemo(() => {
    return subscriptions
      .filter(s => {
        if (!s.next_billing_date || (s.subscription_status !== 'active' && s.subscription_status != null)) {return false;}
        const renewalDate = new Date(s.next_billing_date);
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
        return renewalDate <= thirtyDaysFromNow;
      })
      .sort((a, b) => new Date(a.next_billing_date!).getTime() - new Date(b.next_billing_date!).getTime());
  }, [subscriptions]);

  // Memoize counts
  const activeCount = useMemo(() => activeSubscriptions.length, [activeSubscriptions]);
  const totalCount = useMemo(() => subscriptions.length, [subscriptions]);

  return {
    totalMonthly,
    totalYearly,
    costByCategory,
    upcomingRenewals,
    activeCount,
    totalCount,
  };
};
